// Live sessions whose settlement is not confirmed yet, per signed-in user, in
// localStorage (a tab closed while offline can still settle when the user comes
// back). An entry is removed only once the server confirms the session is settled
// or already closed: never on a failed attempt.
//
// Cross-tab safety: each entry records the tab that owns it and whether it is
// still recording (`active`). The browser only settles entries that were
// EXPLICITLY stopped (Stop, lost connection, leaving the page). An active entry
// is never settled from here, whatever its age: a sleeping or throttled tab looks
// exactly like a closed one, so abandoned active sessions are left to the
// server's stale-session recovery (billed to the last heartbeat). Active entries
// older than STALE_ENTRY_PRUNE_MS are dropped without settling: by then the
// server has certainly recovered them.
//
// Settlement always uses the ORIGINAL session id. It never charges twice: the
// backend replays a settled session and reports a swept one as already closed.

const PREFIX = 'scribe.pendingLiveSessions:';
const LEGACY_KEY = 'scribe.liveSessionId';
// Server session ids are UUIDs; accept any short id-like string (the server validates).
const SESSION_ID_RE = /^[A-Za-z0-9-]{1,64}$/;
export const STALE_ENTRY_PRUNE_MS = 24 * 60 * 60 * 1000;
export const SETTLE_TIMEOUT_MS = 20000;

const randomId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
/** This page load. A reload is a new owner, so its own old entry looks abandoned. */
export const TAB_ID = randomId();

const keyFor = userId => (userId ? `${PREFIX}${userId}` : null);

function read(userId) {
  const key = keyFor(userId);
  if (!key) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed)
      ? parsed.filter(entry => entry && SESSION_ID_RE.test(entry.id || '') && typeof entry.owner === 'string')
      : [];
  } catch (_) {
    return [];
  }
}

function write(userId, entries) {
  const key = keyFor(userId);
  if (!key) return;
  try {
    if (entries.length) localStorage.setItem(key, JSON.stringify(entries));
    else localStorage.removeItem(key);
  } catch (_) { /* storage blocked: the server sweep still settles it */ }
}

function update(userId, sessionId, change) {
  write(userId, read(userId).map(entry => (entry.id === sessionId ? { ...entry, ...change } : entry)));
}

/** A just-opened session, owned by this tab while it records. */
export function rememberLiveSession(userId, sessionId) {
  if (!keyFor(userId) || !SESSION_ID_RE.test(sessionId || '')) return;
  const entries = read(userId).filter(entry => entry.id !== sessionId);
  write(userId, [...entries, { id: sessionId, owner: TAB_ID, active: true, seenAt: Date.now() }]);
}

/** Recording ended locally; settlement may still be pending (any tab may finish it). */
export function markLiveSessionStopped(userId, sessionId) {
  update(userId, sessionId, { active: false, seenAt: Date.now() });
}

export function forgetLiveSession(userId, sessionId) {
  write(userId, read(userId).filter(entry => entry.id !== sessionId));
  try {
    if (sessionStorage.getItem(LEGACY_KEY) === sessionId) sessionStorage.removeItem(LEGACY_KEY);
  } catch (_) {}
}

/** Only explicitly stopped sessions; an active one is the server's to recover. */
export function isSettleable(entry) {
  return !entry.active;
}

/** Drops active entries so old the server has certainly recovered them (not settled). */
export function pruneStaleLiveSessions(userId, now = Date.now()) {
  const entries = read(userId);
  const kept = entries.filter(entry => !entry.active || now - (Number(entry.seenAt) || 0) < STALE_ENTRY_PRUNE_MS);
  if (kept.length !== entries.length) write(userId, kept);
}

/** Ids this tab may settle now, including one left in sessionStorage by an older build. */
export function settleableLiveSessions(userId) {
  const ids = read(userId).filter(isSettleable).map(entry => entry.id);
  try {
    const legacy = sessionStorage.getItem(LEGACY_KEY);
    if (legacy && SESSION_ID_RE.test(legacy) && !ids.includes(legacy)) ids.push(legacy);
  } catch (_) {}
  return ids;
}

export function pendingLiveSessions(userId) {
  return read(userId);
}

/** A settle error that can never succeed on retry (bad or unknown id). */
function isPermanentSettleError(error) {
  return /valid session_id required|not found/i.test(error?.message || '');
}

/**
 * Settles one session with a timeout, so a hanging request never blocks later
 * retries. Resolves 'settled' (incl. a replay), 'already_closed' (backend
 * recovered it first; nothing charged again) or 'pending' (keep it, retry later).
 * Never throws.
 */
export async function settlePendingLiveSession(userId, sessionId, settle, { timeoutMs = SETTLE_TIMEOUT_MS } = {}) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer;
  try {
    const result = await Promise.race([
      settle(sessionId, controller ? { signal: controller.signal } : undefined),
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller?.abort();
          reject(new Error('Live settlement timed out'));
        }, timeoutMs);
      }),
    ]);
    forgetLiveSession(userId, sessionId);
    return result?.already_closed ? 'already_closed' : 'settled';
  } catch (error) {
    if (isPermanentSettleError(error)) {
      forgetLiveSession(userId, sessionId);
      return 'already_closed';
    }
    return 'pending';
  } finally {
    clearTimeout(timer);
  }
}

/** Settles every session this tab may settle (page load, back online). */
export async function reconcilePendingLiveSessions(userId, settle, { except } = {}) {
  pruneStaleLiveSessions(userId);
  const results = {};
  for (const id of settleableLiveSessions(userId)) {
    if (id === except) continue;
    results[id] = await settlePendingLiveSession(userId, id, settle);
  }
  return results;
}
